import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import socket from "../../socket";
import { FaDownload } from "react-icons/fa";
import { toast } from "react-toastify";
import "./HistoryTab.css";

const formatDate = (dateStr) => {
  if (!dateStr) return { date: '-', time: '-' };
  const dateObj = new Date(dateStr);
  if (isNaN(dateObj)) return { date: '-', time: '-' };
  const date = dateObj.toLocaleDateString('en-US', { 
    month: '2-digit', day: '2-digit', year: 'numeric',
    timeZone: 'America/Chicago'  // ← Iowa timezone
  });
  const time = dateObj.toLocaleTimeString('en-US', { 
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: 'America/Chicago'  // ← Iowa timezone
  });
  return { date, time };
};

export default function HistoryTab({ recordId }) {
  const { user } = useAuth();
  const [adminHistory, setAdminHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (recordId) {
      fetchAdminHistory();
      socket.on("history-updated", (data) => {
        if (data.recordId === recordId) fetchAdminHistory();
      });
      return () => socket.off("history-updated");
    }
  }, [recordId]);

  const fetchAdminHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`https://dshb-deposits.its.uiowa.edu/api/admin-history?submission_id=${recordId}`);
      if (!res.ok) {
        const errorData = await res.json();
        setError(errorData.error || "Failed to fetch history");
        setAdminHistory([]);
        return;
      }
      const data = await res.json();
      setAdminHistory(data || []);
    } catch (err) {
      setError(err.message);
      setAdminHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // NEW FUNCTION - Download CSV of history
  const downloadHistoryCSV = () => {
    if (adminHistory.length === 0) { toast.error("No history to export"); return; }

    const headers = ["DATE & TIME", "ADMIN", "ACTION", "FIELD", "OLD VALUE", "NEW VALUE"];

    const rows = adminHistory.map(h => {
      const { date, time } = formatDate(h.timestamp || h.created_at);
      return [
        `"${date} ${time}"`,
        `"${h.admin_name || "Unknown"}"`,
        `"${h.action || ""}"`,
        `"${h.field_name || ""}"`,
        `"${(h.old_value || "").toString().replace(/"/g, '""')}"`,
        `"${(h.new_value || "").toString().replace(/"/g, '""')}"`
      ].join(",");
    });

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `admin-history-${recordId}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success("History CSV downloaded!");
  };

  if (loading) return <div className="no-history">Loading history...</div>;
  if (error) return <div className="no-history" style={{ color: "#d32f2f" }}>Error: {error}</div>;
  if (adminHistory.length === 0) return <div className="no-history">No admin activity available</div>;

  return (
    <div className="history-tab">
      <div className="history-header">
        <span style={{ fontSize: "12px", color: "#9ca3af" }}>
          Showing last {adminHistory.length} activities
        </span>
        
        {/* NEW - CSV Download Button */}
        <button 
          onClick={downloadHistoryCSV}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 12px",
            background: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "600"
          }}
          title="Download history as CSV"
        >
          <FaDownload /> Download CSV
        </button>
      </div>

      <div className="history-table-wrapper">
        <table className="history-table">
          <thead>
            <tr>
              <th>DATE</th>
              <th>SET BY</th>
              <th>FIELD</th>
              <th>OLD VALUE</th>
              <th>NEW VALUE</th>
            </tr>
          </thead>
          <tbody>
            {adminHistory.map((h, idx) => {
              const { date, time } = formatDate(h.timestamp || h.created_at);

              return (
                <tr key={idx}>
                  <td>
                    <div>{date}</div>
                    <div style={{ fontSize: "10px", color: "#9ca3af" }}>{time}</div>
                  </td>
                  <td>{h.admin_name || "Unknown"}</td>
                  <td>
                    {h.field_name ? (
                      <span className="history-field-badge">{h.field_name}</span>
                    ) : (
                      <span style={{ color: "#9ca3af", fontSize: "11px" }}>{h.action}</span>
                    )}
                  </td>
                  <td>
                    {h.old_value ? (
                      <span className="history-old">{h.old_value}</span>
                    ) : "-"}
                  </td>
                  <td>
                    {h.new_value ? (
                      <span className="history-new">{h.new_value}</span>
                    ) : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
